import { useCallback, useState } from 'react';

/**
 * useReportModal - Custom hook for managing report modal state
 *
 * @param {Object} options - Hook options
 * @param {string} options.selectionMode - 'single' or 'multiple' selection mode
 * @param {Function} options.onSubmit - Callback when form is submitted
 * @param {Function} options.onClose - Callback when modal is closed
 * @returns {Object} Modal state and handlers
 */
function useReportModal({ selectionMode = 'single', onSubmit, onClose }) {
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [comment, setComment] = useState('');

  const handleOptionToggle = useCallback(
    (optionId) => {
      setSelectedOptions((prev) => {
        if (selectionMode === 'single') {
          // Single selection - replace previous selection
          return prev.includes(optionId) ? [] : [optionId];
        }
        // Multiple selection - toggle
        if (prev.includes(optionId)) {
          return prev.filter((id) => id !== optionId);
        }
        return [...prev, optionId];
      });
    },
    [selectionMode]
  );

  const handleCommentChange = useCallback((value) => {
    setComment(value);
  }, []);

  const handleSubmit = useCallback(() => {
    if (selectedOptions.length === 0) return;

    onSubmit({
      selectedOptions,
      comment: comment.trim() || null,
    });

    // Reset form after submission
    setSelectedOptions([]);
    setComment('');
  }, [selectedOptions, comment, onSubmit]);

  const handleClose = useCallback(() => {
    setSelectedOptions([]);
    setComment('');
    onClose();
  }, [onClose]);

  const resetForm = useCallback(() => {
    setSelectedOptions([]);
    setComment('');
  }, []);

  const isOptionSelected = useCallback((optionId) => selectedOptions.includes(optionId), [selectedOptions]);

  const isSubmitDisabled = selectedOptions.length === 0;

  return {
    // State
    selectedOptions,
    comment,
    isSubmitDisabled,

    // Handlers
    handleOptionToggle,
    handleCommentChange,
    handleSubmit,
    handleClose,
    resetForm,
    isOptionSelected,
  };
}

export default useReportModal;
